import Icon from "@react-native-vector-icons/material-design-icons/static";
import { DateTime } from "luxon";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { type StyleProp, TouchableOpacity, type ViewStyle } from "react-native";
import { Divider, Text, TextInput } from "react-native-paper";
import type { IconType } from "../../icon";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import CustomDatePickerModal from "./CustomDatePickerModal";

interface DatePickerDialogProps {
	isClearVisible?: boolean;
	mode?: "single" | "range";
	validRange?: {
		startDate?: Date;
		endDate?: Date;
	};
	style?: StyleProp<ViewStyle>;
	onClose?: () => void;
	onOk?: (value: Date | { startTime: Date; endTime: Date } | null) => void;
	onClear?: () => void;
	inputLabel?: string;
	title?: string;
}

const DatePickerDialog = ({
	isClearVisible,
	mode = "single",
	onOk,
	onClose,
	onClear,
	style,
	inputLabel,
	title,
}: DatePickerDialogProps) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const [startValue, setStartValue] = useState<string>("");
	const [finishValue, setFinishValue] = useState<string>("");
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [activeInput, setActiveInput] = useState<"start" | "finish" | null>(
		null,
	);
	const [startError, setStartError] = useState<string>("");
	const [endError, setEndError] = useState<string>("");

	const hasErrors = startError !== "" || endError !== "";

	const getDisplayError = () => {
		if (startError) {
			return startError;
		}
		if (endError) {
			return endError;
		}
		return "";
	};

	const formatDateInput = (value: string): string => {
		const digits = value.replace(/\D/g, "");

		if (digits.length <= 2) {
			return digits;
		}
		if (digits.length <= 4) {
			return `${digits.slice(0, 2)}/${digits.slice(2)}`;
		}
		return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
	};

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	const validateDate = (dateStr: string, type: "start" | "finish"): boolean => {
		const setError = (message: string) => {
			if (type === "start") {
				setStartError(message);
			} else {
				setEndError(message);
			}
		};

		const clearError = () => {
			if (type === "start") {
				setStartError("");
			} else {
				setEndError("");
			}
		};

		if (!dateStr) {
			setError(t("common.datePicker.dateRequiredError"));
			return false;
		}

		const date = DateTime.fromFormat(dateStr, "MM/dd/yyyy");
		if (!date.isValid) {
			setError(t("common.datePicker.invalidDateFormatError"));
			return false;
		}

		if (mode === "range") {
			const otherDate =
				type === "start"
					? DateTime.fromFormat(finishValue, "MM/dd/yyyy")
					: DateTime.fromFormat(startValue, "MM/dd/yyyy");

			if (otherDate.isValid) {
				if (type === "start" && date > otherDate) {
					setError(t("common.datePicker.startAfterEndError"));
					return false;
				}
				if (type === "finish" && date < otherDate) {
					setError(t("common.datePicker.endBeforeStartError"));
					return false;
				}
			}
		}

		clearError();
		return true;
	};

	const handleOpen = (input: "start" | "finish") => {
		setActiveInput(input);
		setIsModalOpen(true);
	};

	const handleClose = () => {
		setIsModalOpen(false);
		setActiveInput(null);
	};

	const handleOk = (
		value: Date | { startTime: Date; endTime: Date } | null,
	) => {
		if (mode === "single" && value instanceof Date) {
			setStartValue(DateTime.fromJSDate(value).toFormat("MM/dd/yyyy"));
		} else if (mode === "range" && value && "startTime" in value) {
			setStartValue(
				DateTime.fromJSDate(value.startTime).toFormat("MM/dd/yyyy"),
			);
			setFinishValue(DateTime.fromJSDate(value.endTime).toFormat("MM/dd/yyyy"));
		}
		handleClose();
	};

	const handleClear = () => {
		setStartValue("");
		setFinishValue("");
		setStartError("");
		setEndError("");
		onClear?.();
	};

	const handleTextChange = (value: string, type: "start" | "finish") => {
		const formattedValue = formatDateInput(value);

		if (type === "start") {
			setStartValue(formattedValue);
			if (formattedValue.length === 10) {
				validateDate(formattedValue, "start");
			} else if (formattedValue) {
				setStartError("");
			} else {
				setStartError(t("common.datePicker.dateRequiredError"));
			}
		} else {
			setFinishValue(formattedValue);
			if (formattedValue.length === 10) {
				validateDate(formattedValue, "finish");
			} else if (formattedValue) {
				setEndError("");
			} else {
				setEndError(t("common.datePicker.dateRequiredError"));
			}
		}
	};

	return (
		<>
			<Box
				backgroundColor={theme.colors.surfaceVariant}
				borderRadius="16px"
				width={328}
			>
				<Box padding={20} paddingBottom={10}>
					<Text
						variant="labelLarge"
						style={{
							color: theme.colors.onSurfaceVariant,
						}}
					>
						{title ?? t("common.datePicker.title")}
					</Text>

					<Box
						display="flex"
						flexDirection="row"
						alignItems="center"
						justifyContent="space-between"
						marginTop={20}
						cursor="pointer"
					>
						<Text
							variant="headlineLarge"
							style={{
								color: theme.colors.onSurface,
							}}
						>
							{mode === "range"
								? t("common.datePicker.enterDateRangeLabel")
								: t("common.datePicker.enterDateLabel")}
						</Text>
						<Icon
							onPress={() => handleOpen("start")}
							name={"calendar-today" as IconType}
							size={24}
							color={theme.colors.onSurfaceVariant}
						/>
					</Box>
				</Box>
				<Divider style={{ borderColor: theme.colors.outlineVariant }} />
				<Box padding={20} display="flex" gap={12} flexDirection="row">
					<TextInput
						style={{ flex: 1 }}
						mode="outlined"
						label={
							mode === "single"
								? t("common.datePicker.dateLabel")
								: t("common.datePicker.startDateLabel")
						}
						outlineColor={
							activeInput === "start" ? theme.colors.primary : undefined
						}
						value={startValue}
						onChangeText={(value) => handleTextChange(value, "start")}
						error={!!startError}
						onPress={() => handleOpen("start")}
					/>
					{mode === "range" && (
						<TextInput
							style={{ flex: 1 }}
							mode="outlined"
							label={t("common.datePicker.endDateLabel")}
							outlineColor={
								activeInput === "finish" ? theme.colors.primary : undefined
							}
							value={finishValue}
							onChangeText={(value) => handleTextChange(value, "finish")}
							error={!!endError}
							onPress={() => handleOpen("finish")}
						/>
					)}
				</Box>
				{hasErrors && (
					<Box paddingHorizontal={20} paddingBottom={12}>
						<Text style={{ color: theme.colors.error }} variant="bodySmall">
							{getDisplayError()}
						</Text>
					</Box>
				)}
				<Box
					display="flex"
					flexDirection="row"
					alignItems="center"
					justifyContent="flex-end"
					paddingTop={8}
					paddingBottom={12}
					paddingRight={12}
					paddingLeft={12}
				>
					{isClearVisible && (
						<TouchableOpacity onPress={handleClear}>
							<Box
								paddingHorizontal={12}
								paddingVertical={10}
								color={theme.colors.primary}
								justifyContent="center"
								alignItems="center"
								height={40}
							>
								<Text
									style={{ color: theme.colors.primary }}
									variant="labelLarge"
								>
									{t("common.clear")}
								</Text>
							</Box>
						</TouchableOpacity>
					)}
					<Box
						display="flex"
						justifyContent="flex-end"
						alignItems="center"
						flex={1}
						flexDirection="row"
					>
						<Box display="flex" gap={8} flexDirection="row">
							<TouchableOpacity onPress={onClose}>
								<Box
									paddingHorizontal={12}
									paddingVertical={10}
									style={{
										height: 40,
										alignItems: "center",
										justifyContent: "center",
									}}
									color={theme.colors.primary}
								>
									<Text
										style={{ color: theme.colors.primary }}
										variant="labelLarge"
									>
										{t("common.cancel")}
									</Text>
								</Box>
							</TouchableOpacity>
							<TouchableOpacity
								onPress={() => {
									if (hasErrors) {
										validateDate(startValue, "start");
										if (mode === "range") {
											validateDate(finishValue, "finish");
										}
									} else {
										onOk?.(
											mode === "single"
												? DateTime.fromFormat(
														startValue,
														"MM/dd/yyyy",
													).toJSDate()
												: {
														startTime: DateTime.fromFormat(
															startValue,
															"MM/dd/yyyy",
														).toJSDate(),
														endTime: DateTime.fromFormat(
															finishValue,
															"MM/dd/yyyy",
														).toJSDate(),
													},
										);
									}
								}}
								disabled={hasErrors}
							>
								<Box
									paddingHorizontal={12}
									paddingVertical={10}
									style={{
										height: 40,
										alignItems: "center",
										justifyContent: "center",
										opacity: hasErrors ? 0.5 : 1,
									}}
									color={theme.colors.primary}
								>
									<Text
										style={{ color: theme.colors.primary }}
										variant="labelLarge"
									>
										{t("common.ok")}
									</Text>
								</Box>
							</TouchableOpacity>
						</Box>
					</Box>
				</Box>
			</Box>
			{isModalOpen && (
				<Box style={style}>
					<CustomDatePickerModal
						mode={mode}
						isClearVisible={isClearVisible}
						onClose={handleClose}
						onOk={handleOk}
						onClear={handleClear}
						startDate={
							startValue
								? DateTime.fromFormat(startValue, "MM/dd/yyyy").toJSDate()
								: undefined
						}
						endDate={
							finishValue
								? DateTime.fromFormat(finishValue, "MM/dd/yyyy").toJSDate()
								: undefined
						}
						label={inputLabel}
					/>
				</Box>
			)}
		</>
	);
};

export default DatePickerDialog;
